import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase, signInWithGoogle, signOut, getCurrentUser, getProfile, updateProfile } from '../utils/supabase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [needsProfileSetup, setNeedsProfileSetup] = useState(false);

  useEffect(() => {
    // Verificar sesión actual
    checkUser();

    // Escuchar cambios de autenticación
    if (supabase) {
      const { data: authListener } = supabase.auth.onAuthStateChange(
        async (event, session) => {
          if (session?.user) {
            setUser(session.user);
            await loadProfile(session.user.id);
            setIsAuthenticated(true);
          } else {
            setUser(null);
            setProfile(null);
            setIsAuthenticated(false);
          }
          setLoading(false);
        }
      );

      return () => {
        authListener?.subscription.unsubscribe();
      };
    } else {
      setLoading(false);
    }
  }, []);

  const checkUser = async () => {
    try {
      const { data } = await getCurrentUser();
      if (data.user) {
        setUser(data.user);
        await loadProfile(data.user.id);
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.error('Error checking user:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProfile = async (userId) => {
    try {
      const { data, error } = await getProfile(userId);
      if (!error && data) {
        setProfile(data);
        // Check if profile is complete
        const isComplete = data.profile_completed === true || 
                          (data.username && data.age && data.country);
        setNeedsProfileSetup(!isComplete);
      } else {
        // Profile doesn't exist yet, needs setup
        setNeedsProfileSetup(true);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      setNeedsProfileSetup(true);
    }
  };

  const login = async (options = {}) => {
    try {
      const { error } = await signInWithGoogle(options);
      if (error) {
        console.error('Error al iniciar sesión:', error);
        return { error };
      }
      return { error: null };
    } catch (error) {
      console.error('Error al iniciar sesión:', error);
      return { error };
    }
  };

  const logout = async () => {
    try {
      const { error } = await signOut();
      if (error) {
        console.error('Error al cerrar sesión:', error);
        return { error };
      }
      setUser(null);
      setProfile(null);
      setIsAuthenticated(false);
      return { error: null };
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
      return { error };
    }
  };

  const updateUserProfile = async (updates) => {
    if (!user) return { error: 'No hay usuario autenticado' };
    
    try {
      const { data, error } = await updateProfile(user.id, updates);
      if (!error && data) {
        setProfile(data);
      }
      return { data, error };
    } catch (error) {
      console.error('Error updating profile:', error);
      return { error };
    }
  };

  const value = {
    user,
    profile,
    loading,
    isAuthenticated,
    needsProfileSetup,
    login,
    logout,
    updateUserProfile,
    refreshProfile: () => user && loadProfile(user.id),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
